import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Agreement, fetchAgreements } from "@/networking/agreements.api";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { Control, useController } from "react-hook-form";
import { useTranslation } from "@/hooks/useTranslation";
import type { CheckoutFormData } from "./checkout.types";
import DropZone from "../drop-zones/drop-zone";

export default function Agreements({
  agreements,
  control,
  onToggle,
  cartToken,
  subdomain,
  companyId,
}: {
  agreements: Agreement[];
  control: Control<CheckoutFormData>;
  onToggle?: () => void;
  cartToken: string;
  subdomain: string | null;
  companyId?: number;
}) {
  const { t } = useTranslation();

  const {
    field,
    fieldState: { error },
  } = useController({
    name: "customer.agreements_accepted",
    control,
  });

  const fetchAgreementMutation = useMutation({
    mutationFn: (id: string) => fetchAgreements(id),
    onError: (error) => {
      console.error("Error fetching agreement", error);
    },
  });

  const [selectedAgreementId, setSelectedAgreementId] = useState<string | null>(
    null,
  );

  const agreementData = fetchAgreementMutation.data;
  const isLoading = fetchAgreementMutation.isPending;

  return (
    <div className="relative z-20">
      <div className="mt-2 flex flex-row items-start space-x-1">
        <div className="flex flex-row items-start">
          <div className="flex h-5 items-center">
            <input
              id="agreements"
              type="checkbox"
              className={cn(
                "h-4 w-4 cursor-pointer rounded border-gray-300 text-black focus:ring-black",
                error && "ring-1 ring-red-500 ring-offset-1",
              )}
              checked={field.value}
              onChange={() => {
                field.onChange(!field.value);
                onToggle?.();
              }}
            />
          </div>
          <div className="ml-3 pt-0.5 text-left text-xs">
            <label
              htmlFor="agreements"
              className="cursor-pointer font-medium text-gray-700"
            >
              {t("agreements_have_read_and_agree")}{" "}
              {agreements.map((agreement, index) => (
                <React.Fragment key={agreement.id}>
                  <span
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer text-xs text-gray-500 underline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!agreement.id) return;
                      const id = agreement.id.toString();
                      setSelectedAgreementId(id);
                      fetchAgreementMutation.mutate(id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!agreement.id) return;
                        const id = agreement.id.toString();
                        setSelectedAgreementId(id);
                        fetchAgreementMutation.mutate(id);
                      }
                    }}
                  >
                    {agreement.title}
                  </span>
                  {index < agreements.length - 2
                    ? ", "
                    : index === agreements.length - 2
                      ? " & "
                      : ""}
                </React.Fragment>
              ))}
              <span className="text-red-500"> *</span>
            </label>
          </div>
        </div>
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-500">
          {t("accept_terms_and_conditions_to_proceed")}
        </p>
      )}
      <DropZone
        zone="below_terms"
        token={cartToken}
        subdomain={subdomain ?? null}
        page="checkout"
        companyId={companyId}
      />
      <Dialog
        open={selectedAgreementId !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedAgreementId(null);
        }}
      >
        <DialogContent className="z-50 h-[80vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {
                agreements.find((a) => a.id?.toString() === selectedAgreementId)
                  ?.title
              }
            </DialogTitle>
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-t-2 border-t-gray-500 border-r-gray-500" />
              </div>
            ) : (
              <iframe
                srcDoc={agreementData?.agreement.description || ""}
                className="h-full w-full border-none"
                title={t("agreement_content")}
              />
            )}
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
